/**
 * 라이더 선정산내역 타입 정의
 * 기반 테이블: vr_rider_acct_daily (또는 선정산 테이블)
 */
import { formatDateToDisplay } from "@/shared/utils/formatter";

/** 라이더 구분 타입 */
export type RiderType = '일반' | '지점장' | '만65세이상';

/** 라이더 선정산내역 Domain Model */
export interface RiderPreSettlement {
    no: number;                     // 1. NO (UI 용)
    supplierName: string;           // 2. 협력사명
    riderId: string;                // 3. 라이더ID
    riderName: string;              // 4. 라이더명
    riderType: RiderType;           // 5. 라이더구분 (일반/지점장/만65세이상)
    deliveryDate: string;           // 6. 배송일자 (=수행일자)
    paymentDate: string;            // 7. 지급일 (배송일자 + 1)
    processCount: number;           // 8. 처리건수
    deliveryFee: number;            // 9. 배달료
    brokerManageFee: number;        // 10. 중가새운영수수료
    partnerOperationFee: number;    // 11. 협력사운영수수료
    totalOperationFee: number;      // 12. 총운영수수료 (중개사 + 협력사)
    taxableDeliveryFee: number;     // 13. 과세배달료 (ROUNDDOWN(배달료, -1) - 총운영수수료)
    monthlySalary: number;          // 14. 월보수액 = ROUND(과세배달료 * 0.802, 0)
    riderEmployment: number;        // 15. 라이더_고용 (조건부: 일반만 부과)
    riderIndustrial: number;        // 16. 라이더_산재 (조건부: 지점장 제외)
    hourlyInsurance: number;        // 17. 시간제보험료
    insuranceTotal: number;         // 18. 총합 (고용+산재+시간제보험료)
    incomeTax: number;              // 19. 소득세
    localTax: number;               // 20. 지방세
    withholdingTax: number;         // 21. 원천징수 (소득세+지방세)
    deductVal: number;              // 22. 일차감
    recoveryAmt: number;            // 23. 회수금
    advanceSettlement: number;      // 24. 선지급배달료
}

/** 라이더 선정산내역 DTO (백엔드 응답) */
export interface RiderPreSettlementDto {
    seqNo: number;
    deliveryDate: string;                   // 배송일자(수행일자)
    paymentDate: string;                    // 지급일 (배송일자 + 1일)
    supplyId: string;
    supplierName?: string;                  // 협력사명
    riderId: string;
    riderName?: string;
    riderType?: string;                     // 라이더구분
    deliveryCnt?: number;                   // 처리건수
    deliveryVal: number;                    // 배달료
    brokerManageFee: number;                // 중개사운영수수료
    partnerManageFee: number;               // 협력사운영수수료
    totalManageFee: number;                 // 총운영수수료
    taxableDeliveryVal: number;             // 과세배달료
    monthlySalary: number;                  // 월보수액
    riderEmpVal: number;                    // 라이더_고용
    riderAccidtVal: number;                 // 라이더_산재
    timeInsurVal: number;                   // 시간제보험료
    insurTotVal: number;                    // 총합
    incomTax: number;                       // 소득세
    localTax: number;                       // 지방세
    withholdTax: number;                    // 원천징수
    deductVal: number;                      // 일차감
    recoverAmt: number;                     // 회수금
    preAcctVal: number;                     // 선지급배달료
    insDate: string;
    insUser: string;
    updDate: string | null;
    updUser: string | null;
}

/** 라이더 선정산내역 목록 조회 응답 DTO */
export interface GetRiderPreSettlementListResponseDto {
    totalCount: number;
    settlements: RiderPreSettlementDto[];
}

/** 최근 정산종료일자 조회 응답 */
export interface GetLatestSettlementDateResponseDto {
    latestStartDate: string;
    latestEndDate: string;
}

/** API 응답 DTO */
export interface ApiResponseDto {
    success: boolean;
    message: string;
}

/** DTO -> Domain 변환 */
export const riderPreSettlementFromDto = (dto: RiderPreSettlementDto, index: number): RiderPreSettlement => {
    // 라이더 구분 (백엔드에서 판단하여 전달, 없으면 일반)
    const riderType = (dto.riderType as RiderType) || '일반';

    return {
        no: index + 1,
        supplierName: dto.supplierName || '',
        riderId: dto.riderId,
        riderName: dto.riderName || dto.riderId,
        riderType: riderType,
        deliveryDate: formatDateToDisplay(dto.deliveryDate),
        paymentDate: formatDateToDisplay(dto.paymentDate),
        processCount: dto.deliveryCnt || 0,
        deliveryFee: dto.deliveryVal || 0,
        brokerManageFee: dto.brokerManageFee || 0,
        partnerOperationFee: dto.partnerManageFee || 0,
        totalOperationFee: dto.totalManageFee || 0,
        taxableDeliveryFee: dto.taxableDeliveryVal || 0,
        monthlySalary: dto.monthlySalary || 0,
        riderEmployment: dto.riderEmpVal || 0,
        riderIndustrial: dto.riderAccidtVal || 0,
        hourlyInsurance: dto.timeInsurVal || 0,
        insuranceTotal: dto.insurTotVal || 0,
        incomeTax: dto.incomTax || 0,
        localTax: dto.localTax || 0,
        withholdingTax: dto.withholdTax || 0,
        deductVal: dto.deductVal || 0,
        recoveryAmt: dto.recoverAmt || 0,
        advanceSettlement: dto.preAcctVal || 0
    };
};

/** 정산내역 목록 변환 */
export const riderPreSettlementListFromDto = (
    response: GetRiderPreSettlementListResponseDto
): RiderPreSettlement[] => {
    return response.settlements.map((dto, index) => riderPreSettlementFromDto(dto, index));
};
